import React, { useState, useEffect } from 'react';
import { apiUrls, getHttpHeaders } from '../config/api';

interface ContentAsset {
  id: number;
  title: string;
  description?: string;
  content_type_id: number;
  subject_id?: number;
  grade_id?: number;
  file_type: string;
  duration_sec?: number;
  is_published: boolean;
  is_active: number;
}

interface ContentType {
  id: number;
  name: string;
}

interface ContentAssetSelectorProps {
  selectedAssetIds: number[];
  onSelectionChange: (selectedIds: number[]) => void;
  gradeId?: string;
  subjectId?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

const ContentAssetSelector: React.FC<ContentAssetSelectorProps> = ({
  selectedAssetIds,
  onSelectionChange,
  gradeId,
  subjectId,
  label = "Content Assets",
  placeholder = "Select Content Assets (Ctrl+Click for multiple)",
  disabled = false
}) => {
  const [contentAssets, setContentAssets] = useState<ContentAsset[]>([]);
  const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch content assets and content types
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log('🔍 Fetching content assets from:', apiUrls.contentAssets.list);
        console.log('🔍 Fetching content types from:', `${apiUrls.books.list}/content-types`);
        
        const [assetsResponse, typesResponse] = await Promise.all([
          fetch(apiUrls.contentAssets.list, { headers: getHttpHeaders() }),
          fetch(`${apiUrls.books.list}/content-types`, { headers: getHttpHeaders() })
        ]);

        console.log('📊 Assets response status:', assetsResponse.status, assetsResponse.statusText);
        console.log('📊 Types response status:', typesResponse.status, typesResponse.statusText);

        if (assetsResponse.ok) {
          const assetsData = await assetsResponse.json();
          console.log('📦 Raw assets data received:', assetsData);
          console.log('📦 Assets array:', assetsData.content);
          setContentAssets(assetsData.content || []);
        } else {
          console.error('❌ Failed to fetch content assets:', assetsResponse.status, assetsResponse.statusText);
          const errorText = await assetsResponse.text();
          console.error('❌ Error response:', errorText);
        }

        if (typesResponse.ok) {
          const typesData = await typesResponse.json();
          console.log('📦 Content types data:', typesData);
          setContentTypes(typesData.contentTypes || []);
        } else {
          console.error('❌ Failed to fetch content types:', typesResponse.status, typesResponse.statusText);
        }
      } catch (err) {
        console.error('Error fetching content data:', err);
        setError('Failed to load content assets');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter content assets based on grade and subject
  const getFilteredAssets = (): ContentAsset[] => {
    console.log('🔍 ContentAssetSelector Debug Info:');
    console.log('📊 Total content assets:', contentAssets.length);
    console.log('🎯 Filter criteria - gradeId:', gradeId, 'subjectId:', subjectId);
    
    // Show all active assets if no filters (for admin content creation)
    if (!gradeId && !subjectId) {
      const filtered = contentAssets.filter(asset => asset.is_active);
      console.log('📋 No filters applied, returning:', filtered.length, 'assets');
      return filtered;
    }
    
    // If only grade is provided, filter by grade only
    if (gradeId && !subjectId) {
      const filtered = contentAssets.filter(asset => {
        // For admin content creation, only check if asset is active (not published status)
        const isActive = asset.is_active;
        const matchesGrade = asset.grade_id?.toString() === gradeId;
        
        console.log(`🔍 Asset: "${asset.title}" | Grade: ${asset.grade_id} | Active: ${asset.is_active} | Published: ${asset.is_published} | Matches Grade: ${matchesGrade}`);
        
        return isActive && matchesGrade;
      });
      
      console.log('✅ Grade-only filtered assets:', filtered.length);
      console.log('📋 Filtered asset titles:', filtered.map(a => a.title));
      return filtered;
    }
    
    // If both grade and subject are provided, filter by both
    const filtered = contentAssets.filter(asset => {
      // For admin content creation, only check if asset is active (not published status)
      if (!asset.is_active) {
        return false;
      }
      
      const matchesGrade = !gradeId || asset.grade_id?.toString() === gradeId;
      const matchesSubject = !subjectId || asset.subject_id?.toString() === subjectId;
      
      console.log(`🔍 Asset: "${asset.title}" | Grade: ${asset.grade_id} (matches: ${matchesGrade}) | Subject: ${asset.subject_id} (matches: ${matchesSubject}) | Active: ${asset.is_active} | Published: ${asset.is_published}`);
      
      return matchesGrade && matchesSubject;
    });
    
    console.log('✅ Final filtered assets:', filtered.length);
    console.log('📋 Filtered asset titles:', filtered.map(a => a.title));
    
    return filtered;
  };

  // Get content type name by ID
  const getContentTypeName = (contentTypeId: number): string => {
    const contentType = contentTypes.find(ct => ct.id === contentTypeId);
    return contentType?.name || 'Unknown';
  };

  // Handle selection change
  const handleSelectionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedIds = Array.from(event.target.selectedOptions, option => Number(option.value));
    onSelectionChange(selectedIds);
  };

  // Get selected asset names for display
  const getSelectedAssetNames = (): string[] => {
    return selectedAssetIds.map(assetId => {
      const asset = contentAssets.find(a => a.id === assetId);
      return asset ? asset.title : `Asset ${assetId}`;
    });
  };

  const filteredAssets = getFilteredAssets();

  if (loading) {
    return (
      <div>
        <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#374151' }}>
          {label}
        </label>
        <div style={{
          padding: '12px',
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          backgroundColor: '#f9fafb',
          color: '#6b7280',
          fontSize: '14px'
        }}>
          Loading content assets...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#374151' }}>
          {label}
        </label>
        <div style={{
          padding: '12px',
          border: '1px solid #ef4444',
          borderRadius: '6px',
          backgroundColor: '#fef2f2',
          color: '#dc2626',
          fontSize: '14px'
        }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#374151' }}>
        {label}
      </label>
      
      <select
        multiple
        value={selectedAssetIds.map(String)}
        onChange={handleSelectionChange}
        disabled={disabled}
        style={{
          width: '100%',
          minHeight: '150px',
          padding: '8px 12px',
          border: '2px solid #3b82f6',
          borderRadius: '6px',
          fontSize: '14px',
          backgroundColor: 'white',
          color: '#1f2937',
          cursor: 'pointer'
        }}
        title="Click to select, Ctrl+Click for multiple"
      >
        {/* Show all content assets - no filtering */}
        {contentAssets.map(asset => (
          <option 
            key={asset.id} 
            value={asset.id} 
            style={{ 
              padding: '6px',
              color: '#1f2937',
              backgroundColor: 'white',
              fontWeight: '500'
            }}
          >
            {asset.title} - {getContentTypeName(asset.content_type_id)}
            {asset.duration_sec ? ` (${Math.floor(asset.duration_sec/60)} min)` : ''}
            {asset.grade_id ? ` [Grade: ${asset.grade_id}]` : ''}
          </option>
        ))}
        
        {contentAssets.length === 0 && (
          <option value="" disabled style={{ color: '#dc2626', fontStyle: 'italic' }}>
            No content assets available. Please upload assets in Content Library first.
          </option>
        )}
      </select>
      
      <div style={{ marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
        Hold Ctrl (Windows) or Cmd (Mac) and click to select multiple assets
      </div>
      
      {/* Info */}
      <div style={{ marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
        {contentAssets.length} asset(s) available
        {(gradeId || subjectId) && ` • Showing all grades and subjects`}
      </div>
      
      {/* Selection feedback */}
      {selectedAssetIds.length > 0 && (
        <div style={{
          marginTop: '8px',
          padding: '8px 12px',
          backgroundColor: '#ecfdf5',
          border: '1px solid #d1fae5',
          borderRadius: '4px',
          fontSize: '12px',
          color: '#059669'
        }}>
          ✅ {selectedAssetIds.length} content asset(s) selected
          <div style={{ marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
            Assets: {getSelectedAssetNames().join(', ')}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentAssetSelector;
